import { GET_PRODUCT_SUBJECTS } from './queries';

const ecommerceEventHandler = (eventDetails, app, windowObject = window) => {
  const { eventName, client } = eventDetails;
  app.log.debug('plugin:ecommerce:event:*', eventDetails);

  const handleEvent = () => {
    try {
      switch (eventName) {
        case 'remove_from_cart':
          return removeFromCart();
        case 'view_cart_review':
          return cartStep();
        case 'purchase':
          return purchase();
        case 'select_promotion':
          return addDiscount();
        default:
          return null;
      }
    } catch (error) {
      app.log.error('Error handling ecommerce event:', error);
    }
  };

  const addDiscount = () => {
    const { cartData, input } = eventDetails;
    const { discounts = [] } = cartData || {};
    const foundDiscount = discounts.find(discount => discount.code === input.code) || {};
    const event = {
      event: 'select_promotion',
      ecommerce: {
        coupon: input.code,
        promotion_id: foundDiscount.id,
        promotion_name: foundDiscount.name
      }
    };
    updateDataLayer(event);
  };

  const getDiscountAmount = value => value * -1;

  const getDiscountCode = () => {
    const { cartData } = eventDetails;
    const { discounts = [] } = cartData || {};

    return discounts.map(discount => discount.code).join(', ');
  };

  const purchase = async () => {
    const { cartData, result: order = {} } = eventDetails;
    const { lineItems = [], discountsTotal, total } = cartData || {};

    const discountCode = getDiscountCode();

    const products = await Promise.all(lineItems.map(getPurchaseProduct));

    const event = {
      event: 'trackPurchase',
      ecommerce: {
        purchase: {
          actionField: {
            id: order.id, // shopware order id
            affiliation: 'Kogan Page',
            revenue: total,
            discount: getDiscountAmount(discountsTotal),
            coupon: discountCode
          },
          product: products.map(({ id, name, price, quantity, format, subjects }) => ({
            id,
            name,
            price,
            quantity,
            format,
            category: subjects.join(', ')
          })),
          items: products.map(
            ({ id, name, price, quantity, formatLabel, discount, subjectNames, subjects }) => ({
              item_id: id,
              item_name: name,
              price,
              quantity,
              item_variant: formatLabel,
              discount,
              coupon: discountCode,
              contentSubject: subjectNames.join(','),
              constendSubjectsWithCodes: subjects.join(',')
            })
          )
        }
      }
    };
    updateDataLayer(event);
  };

  const cartStep = async () => {
    const { cartData } = eventDetails;
    const { lineItems = [], total, currency } = cartData || {};
    const discountCode = getDiscountCode();
    const products = await Promise.all(lineItems.map(getPurchaseProduct));
    const { availableProducts, preorderProducts } = products.reduce(
      (acc, product) => {
        if (product.isPreorder) {
          acc.preorderProducts.push(product);
        } else {
          acc.availableProducts.push(product);
        }
        return acc;
      },
      { availableProducts: [], preorderProducts: [] }
    );
    const event = {
      event_category: 'Checkout',
      event_action: 'Track checkout loaded',
      event_label: total.toString(),
      coupon: discountCode,
      checkout_event_products: availableProducts.map(
        ({ id, name, totalPrice, quantity, format, subjects }) => ({
          isbn: id,
          name,
          price: totalPrice.toString(),
          quantity,
          format,
          category: subjects.join(', ')
        })
      ),
      checkout_event_currency: currency,
      preorder_data: preorderProducts.map(
        ({ id, name, quantity, format, totalPrice, subjects }) => ({
          item_id: id,
          item_name: name,
          item_variant: format,
          price: totalPrice,
          quantity,
          tracking_subject_code: subjects
        })
      ),
      event: 'checkoutLoaded'
    };

    updateDataLayer(event);
  };

  const removeFromCart = () => {
    const { input: [deletedItem] = [], oldCartData: { lineItems = [] } = {} } = eventDetails;

    if (!deletedItem) {
      return;
    }

    const deletedLineItem = lineItems.find(
      item => item.productId === deletedItem.productId && item.variantId === deletedItem.variantId
    );

    if (!deletedLineItem) {
      return;
    }

    const { label, totalPrice, name, quantity, variantId } = deletedLineItem;
    const { productName, isbn } = getIsbnAndName(name);
    const product = {
      id: isbn,
      name: productName,
      price: totalPrice, // old event uses total price
      quantity,
      format: variantId,
      formatLabel: label
    };

    const removeFromCartEvent = {
      event_category: 'Checkout',
      event_action: 'Remove from cart',
      timestamp: new Date().toString(),
      ecommerce: {
        remove: {
          products: [product]
        }
      }
    };

    updateDataLayer(removeFromCartEvent);
  };

  const getPurchaseProduct = async item => {
    const {
      label,
      totalPrice,
      name,
      quantity,
      variantId,
      productId,
      price,
      totalDiscount,
      isPreorder
    } = item;
    const { productName, isbn } = getIsbnAndName(name);
    const { subjects, subjectNames } = await getProductSubjects(productId);
    const product = {
      id: isbn,
      isPreorder,
      name: productName,
      price: typeof price === 'number' ? price.toString() : price.slice(1),
      totalPrice,
      quantity,
      format: variantId,
      formatLabel: label,
      discount: getDiscountAmount(totalDiscount),
      subjects,
      subjectNames
    };
    return product;
  };

  const getProductSubjects = async productId => {
    const { data: { product: { subjects = [] } = {} } = {} } = await client.query({
      query: GET_PRODUCT_SUBJECTS,
      variables: { id: productId }
    });

    return {
      subjects: subjects.map(
        ({ name, description }) => `${description.trim().toUpperCase()}: ${name}`
      ),
      subjectNames: subjects.map(({ name }) => name)
    };
  };

  const getIsbnAndName = name => {
    const [, productName, isbn] = /(.*)\(([0-9]+)\)/.exec(name);
    return { isbn, productName: productName.trim() };
  };

  const updateDataLayer = event => {
    if (windowObject.dataLayer === undefined) {
      return;
    }

    app.log.debug('updateDataLayer', event);
    windowObject.dataLayer.push(event);
  };

  return handleEvent();
};

export default ecommerceEventHandler;
